import { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import type { ChatSource } from '@/types/api.types'
import { MermaidRenderer } from '../MermaidRenderer'
import styles from './MarkdownWithSources.module.css'

export interface MarkdownWithSourcesProps {
  content: string
  sources?: ChatSource[]
  scenario?: string
}

interface SourceReference {
  index: number
  source: ChatSource
}

function getSourceTitle(source: ChatSource): string {
  if (source.title) return source.title
  if (source.file_name) return source.file_name
  if (source.file_path) return source.file_path.split('/').pop() ?? source.file_path
  if (source.issue_key) return source.issue_key
  return 'Source'
}

/**
 * Component to render markdown with inline source citations and mermaid diagrams
 */
export function MarkdownWithSources({ content, sources = [], scenario }: MarkdownWithSourcesProps) {
  // Check if content contains mermaid diagrams
  const hasMermaid = scenario === 'mermaid_generation' || /```mermaid/i.test(content)

  // Split content into text and mermaid blocks
  const blocks = useMemo(() => {
    if (!hasMermaid) {
      return [{ type: 'markdown' as const, content }]
    }

    const parts: Array<{ type: 'markdown' | 'mermaid'; content: string }> = []
    const mermaidRegex = /```mermaid\s*\n([\s\S]*?)\n```/gi
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = mermaidRegex.exec(content)) !== null) {
      // Add text before mermaid block
      if (match.index > lastIndex) {
        const textContent = content.slice(lastIndex, match.index)
        if (textContent.trim()) {
          parts.push({ type: 'markdown', content: textContent })
        }
      }

      // Add mermaid block
      parts.push({ type: 'mermaid', content: match[0] })
      lastIndex = match.index + match[0].length
    }

    // Add remaining text
    if (lastIndex < content.length) {
      const textContent = content.slice(lastIndex)
      if (textContent.trim()) {
        parts.push({ type: 'markdown', content: textContent })
      }
    }

    return parts.length > 0 ? parts : [{ type: 'markdown', content }]
  }, [content, hasMermaid])

  // Create source reference map [1] -> source
  // Use source.id if available (Citation-by-Reference), otherwise fall back to index + 1
  const sourceMap = useMemo<Map<number, SourceReference>>(() => {
    const map = new Map<number, SourceReference>()
    sources.forEach((source, index) => {
      // Try to use source.id first (from Citation-by-Reference system)
      let sourceId: number
      if (source.id) {
        try {
          sourceId = parseInt(source.id, 10)
        } catch {
          // Fallback to index-based ID if parsing fails
          sourceId = index + 1
        }
      } else {
        // Fallback to index-based ID if no id field
        sourceId = index + 1
      }
      map.set(sourceId, { index: sourceId, source })
    })
    return map
  }, [sources])

  // Get URL for a source
  const getSourceUrl = (source: ChatSource): string | null => {
    if (source.url) return source.url
    return null
  }

  // Process markdown text to replace [1], [2], etc. with inline source links
  const processMarkdownWithCitations = (text: string): string => {
    if (sources.length === 0) {
      return text
    }

    // Replace [number] with superscript link to source
    const processed = text.replace(/\[(\d+)\]/g, (match, num: string) => {
      const sourceNum = parseInt(num, 10)
      const ref = sourceMap.get(sourceNum)

      if (ref) {
        const title = getSourceTitle(ref.source)
        const url = getSourceUrl(ref.source)

        if (url) {
          // Create a clickable link to the actual source
          return `<sup><a href="${url}" target="_blank" rel="noopener noreferrer" class="source-citation" title="${title}">[${String(sourceNum)}]</a></sup>`
        } else {
          // No URL available, create a citation that scrolls to source in list
          return `<sup class="source-citation" data-source-index="${String(sourceNum)}" title="${title}">[${String(sourceNum)}]</sup>`
        }
      }

      return match
    })

    return processed
  }

  // Handle click on source citations without URLs (scroll to source list)
  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement

    // Check if clicked element is a source citation without a link
    if (target.tagName === 'SUP' && target.dataset['sourceIndex']) {
      e.preventDefault()
      const sourceIndex = parseInt(target.dataset['sourceIndex'], 10)

      if (sourceMap.has(sourceIndex)) {
        const ref = sourceMap.get(sourceIndex)
        if (!ref) return

        const sourceId = ref.source.id ?? `source-${String(ref.index)}`

        // Scroll to source in the SourcesList component
        const sourceElement = document.querySelector(`[data-source-id="${sourceId}"]`)
        if (sourceElement) {
          sourceElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          // Expand the Sources section if collapsed
          const sourcesCollapse = document.querySelector('[data-testid="sources-collapse"]')
          if (sourcesCollapse) {
            const expandButton = sourcesCollapse.querySelector('.ant-collapse-header')
            if (expandButton instanceof HTMLElement) {
              const hasActiveClass = expandButton.classList.contains('ant-collapse-item-active')
              if (!hasActiveClass) {
                expandButton.click()
              }
            }
          }
        }
      }
    }
  }

  return (
    <div
      className={styles['container']}
      onClick={handleContainerClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleContainerClick(e as unknown as React.MouseEvent<HTMLDivElement>)
        }
      }}
      role="button"
      tabIndex={0}
    >
      {blocks.map((block, index) => {
        const key = `${block.type}-${String(index)}`

        if (block.type === 'mermaid') {
          return <MermaidRenderer key={key} chart={block.content} />
        }

        const processedContent = processMarkdownWithCitations(block.content)

        return (
          <div key={key} className={styles['markdownContent']}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {processedContent}
            </ReactMarkdown>
          </div>
        )
      })}
    </div>
  )
}
